import {
  Component,
  OnInit,
  Input,
  TemplateRef,
  ContentChild,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';

import * as XLSX from 'xlsx/dist/xlsx.mini.min';
import { Angulartics2 } from 'angulartics2';
import { IPagingService } from '@app/core/interfaces/paging-service.interface';
import { SearchEvent } from '@app/core/components/ag1-search/ag1-search.component';
import { NgTemplateOutlet, NgClass, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Ag1ButtonComponent } from '../ag1-button/ag1-button.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Ag1FilterGroupComponent } from '../ag1-filter-group/ag1-filter-group.component';
import { Ag1SearchComponent } from '../ag1-search/ag1-search.component';
import { Ag1InfoComponent } from '../ag1-info/ag1-info.component';
import { Bind } from 'primeng/bind';
import { Popover } from 'primeng/popover';
import { Ag1PillGroupComponent } from '../ag1-pill-group/ag1-pill-group.component';
import { Ag1ButtonSmallComponent } from '../ag1-button-small/ag1-button-small.component';
import { Tooltip } from 'primeng/tooltip';
import { Ag1LoaderComponent } from '../ag1-loader/ag1-loader.component';
import { ShimmerLoaderComponent } from '../../../shared/shimmer-loader/shimmer-loader.component';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';

export interface FieldType {
  displayName?: string; // Used to show the user a different name than the field name
  name: string; // Unique identifier for the field
  checked: boolean; // Used to show/hide the field
  order?: number; // Used to order the fields
  grouped?: boolean; // Is this field grouped
  groupable?: boolean; // Can this field be grouped
  groupOrder?: number; // The order the field is grouped
  sort?: boolean; // TO BE REMOVED ONCE AG1-TABLE IS REMOVED
  sortable?: boolean; // Can this field be sorted
  sortDirection?: 'asc' | 'desc' | null; // The direction the field is sorted
  width?: number; // The width of the column
}

/**
 * @deprecated Do not add new instances of this component. Use 'ag1-table-base' for virtual scrolling or 'ag1-table-base-old' for non-virtual scrolling.
 */
@Component({
  selector: 'ag1-table',
  templateUrl: './ag1-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    FormsModule,
    Ag1ButtonComponent,
    AngularSvgIconModule,
    Ag1FilterGroupComponent,
    Ag1SearchComponent,
    Ag1InfoComponent,
    Bind,
    Popover,
    Ag1PillGroupComponent,
    NgClass,
    Ag1ButtonSmallComponent,
    Tooltip,
    Ag1LoaderComponent,
    ShimmerLoaderComponent,
    DatePipe,
    HumanizePipe,
  ],
})
export class Ag1TableComponent implements OnInit, OnChanges {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private angulartics2 = inject(Angulartics2);

  // let parent know that data can be fetched using the current filters
  @Output()
  filterEvent: EventEmitter<any> = new EventEmitter();

  // let parent know that fields have been updated (sorting, grouping, etc)
  @Output()
  fieldsChange = new EventEmitter<FieldType[]>();

  // let parent know that data can be fetched using the current search params
  @Output()
  searchEvent: EventEmitter<SearchEvent> = new EventEmitter();

  // let parent know that nested data can be fetched for the current row
  @Output()
  expandNestedRowEvent: EventEmitter<any> = new EventEmitter();

  // let parent know that the pageSize has changed
  @Output()
  pageSizeChanged: EventEmitter<number> = new EventEmitter<number>();

  @Input()
  searchFields: string[] = [];

  @Input()
  label: string = '';

  @Input()
  data: [] = [];

  /**
   * A generic paging service to use.
   * Depending on implementation, it may be doing backend or frontend paging
   */
  @Input()
  pagingService: IPagingService;

  private _fields: FieldType[] = [];

  @Input()
  set fields(value: FieldType[]) {
    this._fields = value;
    this.sortFieldsByOrder();
    if (this.data) {
      this.groupFields();
      // Running sort incase there was nothing to group on
      // if already grouped and sorted, sortPending will be false
      // and it will not sort again.
      this.sortColumn(null);
    }
  }
  get fields(): FieldType[] {
    return this._fields;
  }

  @Input()
  isLoading: boolean = true;

  @Input()
  isNestedLoading: boolean = true;

  @Input()
  isEmpty: boolean = false;

  @Input()
  isNestedEmpty: boolean = false;

  @Input()
  emptyMessage: string = 'No data available';

  @Input()
  cumulativeFields: string[] = [];

  @Input()
  downloadable: boolean = false;

  @Input()
  useCustomActions: boolean = false;

  // toggle for animation
  @Input()
  hasLoadingAnimation: boolean = true;

  // add extra information to the table
  @Input()
  infoHeading: string;

  @Input()
  infoBody: string;

  @Input()
  hasFilters: boolean = true;

  @Input()
  hasSearch: boolean = false;

  @Input()
  canGroup: boolean = true;

  @Input()
  editColumns: boolean = true;

  // grouping fields disables nesting
  @Input()
  nested: boolean = false;

  @Input()
  nestedFields: string[] = [];

  @Input()
  nestedData: Record<string, any>[] = [];

  // pass in a custom template
  @ContentChild('customCellContent', { static: false })
  customCellContentRef?: TemplateRef<any>;

  @ContentChild('customActions', { static: false })
  customActionsRef?: TemplateRef<any>;

  @ContentChild('customHeaderContent', { static: false })
  customActionHeaderContent?: TemplateRef<any>;

  @ContentChild('customFooterContent', { static: false })
  customActionFooterContentRef?: TemplateRef<any>;
  // data handling
  groupedData: Record<string, any> = {};
  groupedFieldKeys: string[] = [];
  numSelected: number = 0;

  // sorting
  sortedField: string;
  sortDirection: 'asc' | 'desc';
  sortPending: boolean = false;
  groupPending: boolean = false;

  hasGroupedFields: boolean = false;

  // paging
  selectedPageSize: number = null;
  pageSizes: number[] = [10, 20, 50, 100];

  dataLength: number;

  // object handling
  Object = Object;

  lastUpdated: string;

  expandedRowIndex: number = null;

  constructor() {}

  toggleNestedData(item, rowIndex: number) {
    this.nestedData = [];

    if (this.expandedRowIndex === rowIndex) {
      this.expandedRowIndex = null;
      return;
    }

    // set the active row index
    this.expandedRowIndex = rowIndex;

    // emit the event to fetch the nested data
    this.expandNestedRowEvent.emit(item);
  }

  closeNestedData() {
    this.expandedRowIndex = null;
  }

  nextPage() {
    this.pagingService.goToNextPage();
    if (this.nested) {
      this.closeNestedData();
    }
  }

  previousPage() {
    this.pagingService.goToPrevPage();
    if (this.nested) {
      this.closeNestedData();
    }
  }

  setPageSize() {
    this.isLoading = true;
    this.pagingService.currentPageSize = Number(this.selectedPageSize);
    this.pagingService.refetch();
    this.pageSizeChanged.emit(Number(this.selectedPageSize));
  }

  ngOnInit() {
    this.checkedFields();
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (Object.prototype.hasOwnProperty.call(changes, propName)) {
        switch (propName) {
          case 'data':
            this.lastUpdated = new Date().toISOString();
            if (changes.data.currentValue?.length) {
              this.dataLength = this.data?.length;
              this.data = changes.data.currentValue;
              this.groupPending = true;
              this.groupFields();
            }

            break;
        }
      }
    }
  }

  // trackBys
  // used to set unique identifiers to large iterables to improve performance
  // see https://angular.io/api/core/TrackByFunction
  trackByField(_index, field) {
    return field.name;
  }

  trackByItem(_index, item) {
    return item.id;
  }

  // grouping
  groupByMultiple(
    array: Record<string, any>[],
    groupKeys: string[],
  ): Record<string, any> {
    return array.reduce((groupedData, item) => {
      // create grouping key based on values of keys provided
      const groupKey = JSON.stringify(
        groupKeys.map((key) => item[key]).join(' | '),
      );

      // group on key
      groupedData[groupKey] = [...(groupedData[groupKey] || []), item];
      return groupedData;
    }, {});
  }

  groupedFieldChange(field): void {
    // ngModel two way binding caused a delay in showing that
    // a checkbox was checked due to all the ui gymnastics that
    // is involved in rendering grouped and cumulative data
    // to keep the checkbox in sync the object is manully updated
    // with the newest check state.
    // 👇 manually update the passed in obj ref
    field.grouped = !field.grouped;
    this.fieldsChange.emit(this.fields);
    this.groupFields();
  }

  groupFields(): void {
    // update template group header labels
    // Only regroup if there are new grouped fields
    const newGroupFieldKeys = this.getActiveGroupedFieldKeys(this.fields);
    const noNewGroupFieldKeys =
      newGroupFieldKeys.length === this.groupedFieldKeys.length &&
      newGroupFieldKeys.every((key) => this.groupedFieldKeys.includes(key));
    if (
      noNewGroupFieldKeys &&
      Object.keys(this.groupedData).length &&
      !this.groupPending
    ) {
      return;
    } else {
      if (this.hasLoadingAnimation) {
        // show loading animation when grouping
        this.isLoading = true;
      }
      this.groupedFieldKeys = newGroupFieldKeys;
    }
    this.groupPending = false;

    // update ui with grouped field template
    this.hasGroupedFields = !!this.groupedFieldKeys.length;

    // group table data on selected fields
    this.groupedData = this.groupByMultiple(this.data, this.groupedFieldKeys);

    // If we have regrouped the data, it needs to be sorted again, even if on the same column
    this.sortPending = true;
    this.sortColumn(null);
    this.checkedFields();

    // remove loading state once ui has rendered.
    // rendering occurs before this block due to
    // moving this to the bottom of the call stack
    setTimeout(() => {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    });
  }

  getActiveGroupedFieldKeys(fields: FieldType[]): string[] {
    return fields.reduce((arr, field) => {
      if (field.grouped) {
        arr.push(field.name);
      }
      return arr;
    }, []);
  }

  // sorting
  sortColumn(field: FieldType | null): void {
    const findSortField = (sortField: FieldType | null): FieldType | null => {
      if (sortField) {
        if (sortField.sort) {
          sortField.sortDirection =
            sortField.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          this.removeSort();
          sortField.sort = true;
          sortField.sortDirection = 'asc';
        }
        this.fieldsChange.emit(this.fields);
        return sortField;
      } else {
        return this.fields.find((field) => field.sort);
      }
    };
    const sortField = findSortField(field);

    if (!sortField) return;
    // Do not sort again if sorting does not need to occur
    if (
      this.sortedField === sortField.name &&
      this.sortDirection === sortField.sortDirection &&
      !this.sortPending
    )
      return;

    this.sortedField = sortField.name;
    this.sortDirection = sortField.sortDirection;
    this.sortPending = false;

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });

    const keys = Object.keys(this.groupedData);

    // sort group by field
    keys.forEach((key) => {
      if (this.sortDirection === 'asc') {
        this.groupedData[key].sort((a, b) =>
          collator.compare(
            // check value if null/undefined sort as if ''
            a[this.sortedField] ?? '',
            // check value if null/undefined sort as if ''
            b[this.sortedField] ?? '',
          ),
        );
      } else {
        // Changed from reversing, as occationally opposite direction can be
        // passed in as a value from the parent component
        this.groupedData[key].sort((a, b) =>
          collator.compare(
            // check value if null/undefined sort as if ''
            b[this.sortedField] ?? '',
            // check value if null/undefined sort as if ''
            a[this.sortedField] ?? '',
          ),
        );
      }
    });

    // sort groups by first item value
    this.groupedData = keys
      .sort((a, b) => {
        if (this.sortDirection === 'asc') {
          return collator.compare(
            this.groupedData[a][0][this.sortedField],
            this.groupedData[b][0][this.sortedField],
          );
        } else {
          return collator.compare(
            this.groupedData[b][0][this.sortedField],
            this.groupedData[a][0][this.sortedField],
          );
        }
      })
      .reduce((obj, key) => {
        obj[key] = this.groupedData[key];
        return obj;
      }, {});
  }

  removeSort(): void {
    const sortField = this.fields.find((field) => field.sort);
    if (sortField) {
      sortField.sort = false;
      sortField.sortDirection = 'asc';
    }
  }

  sortFieldsByOrder(): void {
    this.fields.sort((c, d) =>
      c.order > d.order ? 1 : d.order > c.order ? -1 : 0,
    );
  }

  checkedFields(): void {
    this.numSelected = this.fields.filter((field) => field.checked).length;
  }

  updateTable(): void {
    this.sortFieldsByOrder();
    this.checkedFields();
    this.fieldsChange.emit(this.fields);
  }

  updateColumnOrder(e: Event, fromIndex: number) {
    const target = e.target as HTMLFormElement;
    const toIndex = target.value;
    const element = this.fields[fromIndex];
    this.fields.splice(fromIndex, 1);
    this.fields.splice(toIndex, 0, element);
    this.fields.forEach((field, index) => {
      field.order = index;
    });
    this.sortFieldsByOrder();
    this.checkedFields();
    this.fieldsChange.emit(this.fields);
  }

  applyFilters(): void {
    this.isLoading = true;
    this.filterEvent.emit();
  }

  applySearch(searchEvent: SearchEvent): void {
    this.isLoading = true;
    this.searchEvent.emit(searchEvent);
  }

  exportToXLSX(): void {
    XLSX.writeFile(
      XLSX.utils.table_to_book(document.getElementById('table')),
      `${this.label}.xlsx`,
    );
    this.angulartics2.eventTrack.next({
      action: `Export ${this.label}`,
    });
  }
}
